'use client';

import { useState, useEffect, useRef } from 'react';
import { getLanguage, setLanguage } from '@/utils/localStorageControl';
import { LANGUAGES } from '@/language';

const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('ko');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const lang = getLanguage();
    setCurrentLang(lang);

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const changeLang = (lang: string) => {
    setLanguage(lang);
    setCurrentLang(lang);
    setIsOpen(false);
    window.location.reload();
  };

  // 언어 선택 메뉴에서 보여줄 언어명을 해당 언어로 표시
  const getLangDisplay = (lang: string) => {
    // 각 언어는 현재 선택된 언어와 상관없이 자기 언어로 표시
    const langMap: {[key: string]: string} = {
      'ko': '한국어',
      'en': 'English',
      'ja': '日本語',
      'ch': '中文',
      'ru': 'Русский'
    };
    return langMap[lang] || langMap['ko'];
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        <span className="text-lg font-medium">🌐</span>
        <span className="font-medium">{getLangDisplay(currentLang)}</span>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-40 mt-1 w-40 rounded-md shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="py-1">
            <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              {/* 헤더 타이틀은 현재 선택된 언어로 표시 */}
              {(() => {
                // Type guard function to check if the key exists in the object
                const hasKey = <T extends object>(obj: T, key: string | number | symbol): key is keyof T => {
                  return key in obj;
                };
                
                if (LANGUAGES.LANGUAGE_SELECTOR && 
                    hasKey(LANGUAGES.LANGUAGE_SELECTOR, '언어 선택') && 
                    hasKey(LANGUAGES.LANGUAGE_SELECTOR['언어 선택'], currentLang)) {
                  return LANGUAGES.LANGUAGE_SELECTOR['언어 선택'][currentLang];
                }
                return '언어 선택';
              })()}
            </div>
            {['ko', 'en', 'ja', 'ch', 'ru'].map((lang) => (
              <button
                key={lang}
                onClick={() => changeLang(lang)}
                className={`block w-full text-left px-4 py-2 text-sm ${
                  currentLang === lang 
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {/* 각 언어는 해당 언어로 표시 (현재 선택된 언어와 관계없이) */}
                {getLangDisplay(lang)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;